import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';

@Component({
  selector: 'app-booking-filter-modal',
  templateUrl: './booking-filter-modal.component.html',
  styleUrls: ['./booking-filter-modal.component.scss'],
})
export class BookingFilterModalComponent implements OnInit {
  @Input() filters: any[];
  @Input() filterSubject: Subject<any>;
  @Input() selectedFilters: { [key: string]: any[] };

  expandedFilters: { [key: string]: boolean } = {};
  selectedSegment: string = '';
  searchTerms: { [key: string]: string } = {};
  searchActive: { [key: string]: boolean } = {};

  private searchableFilters = [
    'IF!boarding_points',
    'IF!dropping_points',
    'IF!bus_operator',
  ];

  skeletonItems = [
    { facets: [{ labelWidth: '60%' }, { labelWidth: '45%' }, { labelWidth: '70%' }, { labelWidth: '55%' }] },
    { facets: [{ labelWidth: '50%' }, { labelWidth: '65%' }, { labelWidth: '40%' }, { labelWidth: '75%' }] },
    { facets: [{ labelWidth: '55%' }, { labelWidth: '48%' }] },
    { facets: [{ labelWidth: '62%' }] },
  ];

  private buttonStyleFilters = [
    'onward!IF!stops', 'return!IF!stops',
    'onward!IF!dept_time_range', 'onward!IF!arr_time_range',
    'return!IF!dept_time_range', 'return!IF!arr_time_range',
    'Departure Time Range', 'Arrival Time Range',
  ];

  public currentHeaderData: any = {
    modalSlotEnd: true,
    title: 'dutyfree.product_list.filter_modal.header',
    marl20: true,
    mart4: true,
  };

  constructor(
    private modalController: ModalController,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.filters && this.filters.length > 0) {
      this.selectedSegment = this.filters[0]?.name || '';
    }
  }

  closeModal() {
    this.analyticsService.trackUnified('filter_modal_closed', { flow: 'shared', outcome: 'success', legacy_event_name: 'Filter Modal Closed', component: 'filter-modal-component' });
    this.modalController.dismiss();
  }

  resetFilter() {
    this.analyticsService.trackUnified('filter_reset_clicked', { flow: 'shared', outcome: 'success', legacy_event_name: 'Reset Filter Button Clicked', component: 'filter-modal-component' });
    this.selectedFilters = {};
    this.modalController.dismiss({ selectedFilters: {} });
  }

  applyFilter() {
    this.selectedFilters = Object.entries(this.selectedFilters).reduce(
      (acc, [key, value]) => { if (value.length > 0) acc[key] = value; return acc; },
      {}
    );
    this.analyticsService.trackUnified('filter_apply_clicked', {
      flow: 'shared',
      outcome: 'success',
      legacy_event_name: 'Apply Filter Button Clicked',
      component: 'filter-modal-component',
      selected_filters: this.selectedFilters,
    });
    this.modalController.dismiss({ selectedFilters: this.selectedFilters });
  }

  updateFilter(event: any, key: string, facet: string) {
    if (event.target.checked) {
      if (!this.selectedFilters[key]) this.selectedFilters[key] = [];
      this.selectedFilters[key].push(facet);
    } else {
      this.selectedFilters[key] = this.selectedFilters[key].filter((x) => x !== facet);
    }
  }

  onSegmentChange(name: any) {
    this.selectedSegment = name;
    this.analyticsService.trackUnified('filter_tab_selected', { flow: 'shared', outcome: 'success', legacy_event_name: 'Filter Tab Selected', component: 'filter-modal-component', tab_name: name });
    const element = document.getElementById(name);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  isButtonStyle(indexField: string, name?: string): boolean {
    const key = (indexField || '').toString();
    const label = (name || '').toString();
    if (this.buttonStyleFilters.includes(key) || this.buttonStyleFilters.includes(label)) return true;
    const combined = `${key} ${label}`.toLowerCase();
    return combined.includes('dept_time_range') || combined.includes('arr_time_range') || combined.includes('departure time') || combined.includes('arrival time');
  }

  getFacetsToDisplay(filterItem: any) {
    return this.expandedFilters[filterItem.indexField] ? filterItem.facets : (filterItem.facets || []).slice(0, 4);
  }

  shouldShowToggle(filterItem: any): boolean {
    return Array.isArray(filterItem?.facets) && filterItem.facets.length > 4;
  }

  toggleShowAll(filterItem: any) {
    const key = filterItem.indexField;
    this.expandedFilters[key] = !this.expandedFilters[key];
    this.cdr.markForCheck();
  }

  isSearchable(filterItem: any): boolean {
    const isSearchableFilter = this.searchableFilters.some((field) => filterItem.indexField?.includes(field));
    const hasEnoughFacets = filterItem.facets && filterItem.facets.length > 5;
    return (isSearchableFilter && filterItem.facets && filterItem.facets.length > 4) || hasEnoughFacets;
  }

  onSearchFocus(filterItem: any) {
    this.searchActive[filterItem.indexField] = true;
    this.expandedFilters[filterItem.indexField] = true;
  }

  onSearchBlur(filterItem: any) {
    const key = filterItem.indexField;
    setTimeout(() => {
      if (!this.searchTerms[key]) { this.searchActive[key] = false; this.cdr.markForCheck(); }
    }, 200);
  }

  clearSearch(filterItem: any) {
    this.searchTerms[filterItem.indexField] = '';
    this.searchActive[filterItem.indexField] = false;
  }

  getFilteredFacets(filterItem: any): any[] {
    const key = filterItem.indexField;
    const searchTerm = (this.searchTerms[key] || '').toLowerCase().trim();
    let facets = filterItem.facets || [];
    if (searchTerm) facets = facets.filter((facet) => facet.name?.toLowerCase().includes(searchTerm));
    return facets;
  }

  getFacetsToDisplayWithSearch(filterItem: any): any[] {
    const key = filterItem.indexField;
    const isSearching = this.searchActive[key] || this.searchTerms[key];
    const filtered = this.getFilteredFacets(filterItem);
    return (isSearching || this.expandedFilters[key]) ? filtered : filtered.slice(0, 4);
  }

  shouldShowToggleWithSearch(filterItem: any): boolean {
    const key = filterItem.indexField;
    if (this.searchActive[key] || this.searchTerms[key]) return false;
    return Array.isArray(filterItem?.facets) && filterItem.facets.length > 4;
  }

  toggleButtonFilter(indexField: string, facetKey: string) {
    if (!this.selectedFilters[indexField]) this.selectedFilters[indexField] = [];
    const index = this.selectedFilters[indexField].indexOf(facetKey);
    if (index > -1) this.selectedFilters[indexField].splice(index, 1);
    else this.selectedFilters[indexField].push(facetKey);
  }

  isSelected(indexField: string, facetKey: string): boolean {
    return this.selectedFilters[indexField]?.includes(facetKey) || false;
  }

  removeTimeOfDayLabel(name: string = ''): string | null {
    const labels = ['Morning', 'Afternoon', 'Evening', 'Night'];
    if (!labels.some((l) => new RegExp(`\\b${l}\\b`, 'i').test(name))) return null;
    labels.forEach((l) => { name = name.replace(new RegExp(`\\b${l}\\b`, 'i'), ''); });
    return name.trim();
  }

  getTimeOfDayLabel(name: string = ''): string {
    const match = name.match(/^([^\(]+)/);
    return match ? match[1].trim() : '';
  }

  getFacetIcon(facet: any, indexField: string): string | null {
    if (!facet?.iconDTO || !Array.isArray(facet?.iconDTO) || facet?.iconDTO?.length === 0) return null;
    const isSelectedItem = this.isSelected(indexField, facet?.facetKey);
    const theme = isSelectedItem ? 'dark' : 'default';
    const icon = facet.iconDTO.find((i) => i?.theme === theme);
    return icon?.value || facet.iconDTO?.[0]?.value || null;
  }
}
